import { useMemo } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import { Skeleton } from '../ui/skeleton'
import { useOrgCapexPlans } from '../../hooks/useCapexQueries'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import type { WorkflowStatus } from './types'
import { PageHeader } from '../shared/PageHeader'
import { SECTION_ICONS } from '../../lib/section-icons'
import { EmptyState } from '../ui/empty-state'
import { mdiArrowRight  } from '@mdi/js'

const WORKFLOW_BADGE_VARIANTS: Record<WorkflowStatus, 'secondary' | 'default' | 'destructive'> = {
  draft: 'secondary',
  submitted: 'default',
  approved: 'default',
  rejected: 'destructive',
}

interface CapexPlansPageProps {
  org: string
  workflowStatus?: string
  archived?: boolean
}

export function CapexPlansPage({ org, workflowStatus, archived }: CapexPlansPageProps) {
  const { t } = useTranslation('capex')
  const navigate = useNavigate()

  const breadcrumbs = useMemo(
    () => [
      { title: t('common:nav.home'), path: `/${org}` },
      { title: t('nav_title') },
    ],
    [org, t]
  )
  useBreadcrumbs(breadcrumbs)

  const { data: plans, isLoading } = useOrgCapexPlans(org, {
    workflowStatus,
    archived,
  })

  const handleStatusFilter = (value: string) => {
    navigate({
      to: '/$org/capex-plans',
      params: { org },
      search: {
        workflow_status: value === 'all' ? undefined : value,
        archived,
      },
    })
  }

  const handleArchivedToggle = (checked: boolean) => {
    navigate({
      to: '/$org/capex-plans',
      params: { org },
      search: {
        workflow_status: workflowStatus,
        archived: checked || undefined,
      },
    })
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <PageHeader
        icon={SECTION_ICONS.capex}
        title={t('nav_title')}
        description={t('org_list.subtitle')}
      />

      {/* Toolbar */}
      <div className="flex items-center gap-4 flex-wrap">
        <Select
          value={workflowStatus || 'all'}
          onValueChange={handleStatusFilter}
        >
          <SelectTrigger className="w-44" data-testid="capex-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('org_list.filter_all')}</SelectItem>
            <SelectItem value="draft">{t('workflow.draft')}</SelectItem>
            <SelectItem value="submitted">{t('workflow.submitted')}</SelectItem>
            <SelectItem value="approved">{t('workflow.approved')}</SelectItem>
            <SelectItem value="rejected">{t('workflow.rejected')}</SelectItem>
          </SelectContent>
        </Select>

        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
          <input
            type="checkbox"
            checked={archived ?? false}
            onChange={(e) => handleArchivedToggle(e.target.checked)}
            className="rounded border-border"
            data-testid="capex-archived-toggle"
          />
          {t('org_list.show_archived')}
        </label>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && plans.length === 0 && (
        <EmptyState
          className="py-16"
          icon={SECTION_ICONS.capex}
          title={t('org_list.no_plans')}
          description={t('org_list.no_plans_hint')}
        />
      )}

      {/* Table */}
      {!isLoading && plans.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('org_list.col_plan')}</TableHead>
              <TableHead>{t('org_list.col_asset')}</TableHead>
              <TableHead>{t('org_list.col_horizon')}</TableHead>
              <TableHead>{t('org_list.col_status')}</TableHead>
              <TableHead>{t('org_list.col_version')}</TableHead>
              <TableHead className="w-20" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {plans.map((plan) => (
              <TableRow key={plan.uid} data-testid={`capex-plan-row-${plan.uid}`}>
                <TableCell className="font-medium">
                  <Link
                    to="/$org/capex-plans/$planUid"
                    params={{ org, planUid: plan.uid! }}
                    className="hover:underline"
                    data-testid={`capex-plan-link-${plan.uid}`}
                  >
                    {plan.name}
                  </Link>
                </TableCell>
                <TableCell>
                  {plan.asset_uid && (
                    <Link
                      to="/$org/assets/$assetUid"
                      params={{ org, assetUid: plan.asset_uid! }}
                      search={{ tab: 'capex' }}
                      className="hover:underline text-muted-foreground"
                      data-testid={`capex-asset-link-${plan.uid}`}
                    >
                      {(plan as any).asset_name || plan.asset_uid}
                    </Link>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {plan.horizon_start}–{plan.horizon_end}
                </TableCell>
                <TableCell>
                  <Badge
                    variant={
                      WORKFLOW_BADGE_VARIANTS[
                        (plan.workflow_status as WorkflowStatus) ?? 'draft'
                      ]
                    }
                  >
                    {t(`workflow.${plan.workflow_status}`, plan.workflow_status ?? '')}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge variant="outline">v{plan.version}</Badge>
                </TableCell>
                <TableCell>
                  <Link
                    to="/$org/capex-plans/$planUid"
                    params={{ org, planUid: plan.uid! }}
                    data-testid={`capex-plan-open-${plan.uid}`}
                  >
                    <Button variant="ghost" size="sm">
                      <AppIcon path={mdiArrowRight} size="sm" />
                    </Button>
                  </Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  )
}
